/**
 * Export Strategies - Different methods for saving exported photos
 * 
 * Four strategies:
 * 1. SaveWithOriginalFilesStrategy - Write to original folder using File System Access API
 * 2. DownloadToFolderStrategy - Write to user-selected folder using File System Access API
 * 3. DownloadAllStrategy - Download each photo individually
 * 4. DownloadAsZipStrategy - Bundle all photos into a single zip file
 */

import ExportCacheManager from './export_cache_manager.js';

/**
 * Base Strategy Class
 */
class ExportStrategy {
  constructor(editorState) {
    this.editorState = editorState;
    this.exportCacheManager = ExportCacheManager;
  }

  /**
   * Check if this strategy is available
   * @returns {Promise<{available: boolean, reason: string}>}
   */
  async checkAvailability() {
    return { available: true, reason: '' };
  }

  /**
   * Initialize strategy before export starts
   */
  async initialize() {
    // Override in subclass
  }

  /**
   * Process a single exported photo
   * @param {object} queueItem - Export queue item with photo info
   * @param {Blob} blob - Rendered image blob
   * @param {string} exportFilename - Computed export filename
   */
  async processPhoto(queueItem, blob, exportFilename) {
    throw new Error('processPhoto must be implemented by subclass');
  }

  /**
   * Finalize strategy after all photos processed
   */
  async finalize() {
    // Override in subclass if needed
  }

  /**
   * Apply filename pattern to generate export filename
   * @param {string} pattern - Filename pattern (e.g., '{filename}_tg')
   * @param {object} photo - Photo object
   * @returns {string} Computed filename
   */
  applyFilenamePattern(pattern, photo) {
    const originalFilename = photo.original_filename || 'photo.jpg';
    const parts = originalFilename.split('.');
    const ext = parts.pop();
    const basename = parts.join('.');
    
    const now = new Date();
    const dateStr = now.toISOString().split('T')[0].replace(/-/g, '');
    const timeStr = now.toTimeString().split(' ')[0].replace(/:/g, '');
    
    let filename = pattern
      .replace('{filename}', basename)
      .replace('{date}', dateStr)
      .replace('{time}', timeStr);
    
    // Ensure we have extension
    if (!filename.endsWith(`.${ext}`)) {
      filename += `.${ext}`;
    }
    
    return filename;
  }
}

/**
 * Strategy 1: Save With Original Files
 * Write exported files next to originals using File System Access API
 */
export class SaveWithOriginalFilesStrategy extends ExportStrategy {
  async checkAvailability() {
    // Check if File System Access API is available
    if (!window.showDirectoryPicker) {
      return { available: false, reason: 'File System Access API not supported in this browser' };
    }
    
    // Check if all queue items are from granted folders
    const state = this.editorState.get();
    const queueItems = state.exportQueueItems.filter(i => i.status === 'pending');
    
    // Need to check if photos are from granted folders
    // This would require folder information from state
    // For now, just check API availability
    
    return { available: true, reason: '' };
  }

  async processPhoto(queueItem, blob, exportFilename) {
    const photo = queueItem.photo;
    
    // Get folder handle for this photo's folder
    // This would need to be retrieved from FolderManager/StateManager
    // For now, throw error indicating implementation needed
    throw new Error('SaveWithOriginalFilesStrategy requires folder handle integration');
    
    // Implementation would be:
    // const folderHandle = await getFolderHandleForPhoto(photo);
    // const fileHandle = await folderHandle.getFileHandle(exportFilename, { create: true });
    // const writable = await fileHandle.createWritable();
    // await writable.write(blob);
    // await writable.close();
  }
}

/**
 * Strategy 2: Download to Folder
 * Write exported files to user-selected folder using File System Access API
 */
export class DownloadToFolderStrategy extends ExportStrategy {
  constructor(editorState) {
    super(editorState);
    this.folderHandle = null;
  }

  async checkAvailability() {
    if (!window.showDirectoryPicker) {
      return { available: false, reason: 'File System Access API not supported in this browser' };
    }
    return { available: true, reason: '' };
  }

  async initialize() {
    // Check if we already have a folder handle
    const state = this.editorState.get();
    if (state.exportTargetFolderHandle) {
      this.folderHandle = state.exportTargetFolderHandle;
      
      // Verify permission
      const permission = await this.folderHandle.queryPermission({ mode: 'readwrite' });
      if (permission === 'granted') {
        return;
      }
      
      // Request permission if not granted
      const newPermission = await this.folderHandle.requestPermission({ mode: 'readwrite' });
      if (newPermission !== 'granted') {
        throw new Error('Write permission denied for export folder');
      }
    } else {
      // Prompt user to select folder
      this.folderHandle = await window.showDirectoryPicker({ mode: 'readwrite' });
      
      // Save handle to state
      this.editorState.set({ exportTargetFolderHandle: this.folderHandle });
    }
  }

  async processPhoto(queueItem, blob, exportFilename) {
    if (!this.folderHandle) {
      throw new Error('Folder handle not initialized');
    }
    
    try {
      // Create file in the folder
      const fileHandle = await this.folderHandle.getFileHandle(exportFilename, { create: true });
      const writable = await fileHandle.createWritable();
      await writable.write(blob);
      await writable.close();
      
      console.log(`[DownloadToFolderStrategy] Wrote ${exportFilename}`);
    } catch (error) {
      console.error('[DownloadToFolderStrategy] Error writing file:', error);
      throw error;
    }
  }
}

/**
 * Strategy 3: Download All
 * Download each photo individually as it's rendered
 */
export class DownloadAllStrategy extends ExportStrategy {
  async checkAvailability() {
    // Always available
    return { available: true, reason: '' };
  }

  async processPhoto(queueItem, blob, exportFilename) {
    console.log(`[DownloadAllStrategy] >>> processPhoto: ${exportFilename}, blob size: ${blob.size}`);
    // Create blob URL
    const blobUrl = URL.createObjectURL(blob);
    
    try {
      // Create temporary link and click it
      console.log(`[DownloadAllStrategy] Creating download link...`);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = exportFilename;
      a.style.display = 'none';
      document.body.appendChild(a);
      console.log(`[DownloadAllStrategy] Clicking download link...`);
      a.click();
      
      // Cleanup after a delay to ensure download starts
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      }, 1000);
      
      console.log(`[DownloadAllStrategy] <<< Downloaded ${exportFilename}`);
    } catch (error) {
      URL.revokeObjectURL(blobUrl);
      throw error;
    }
  }
}

/**
 * Strategy 4: Download as Zip
 * Bundle all photos into a single zip file
 */
export class DownloadAsZipStrategy extends ExportStrategy {
  constructor(editorState) {
    super(editorState);
    this.zip = null;
    this.JSZip = null;
  }

  async checkAvailability() {
    // Always available (JSZip will be lazy-loaded)
    return { available: true, reason: '' };
  }

  async initialize() {
    // Lazy load JSZip
    console.log('[DownloadAsZipStrategy] Loading JSZip...');
    const module = await import('jszip');
    this.JSZip = module.default;
    this.zip = new this.JSZip();
    console.log('[DownloadAsZipStrategy] JSZip loaded');
  }

  async processPhoto(queueItem, blob, exportFilename) {
    if (!this.zip) {
      throw new Error('Zip not initialized');
    }
    
    // Add file to zip (streaming to avoid memory issues)
    this.zip.file(exportFilename, blob, { binary: true });
    
    console.log(`[DownloadAsZipStrategy] Added ${exportFilename} to zip`);
  }

  async finalize() {
    if (!this.zip) {
      throw new Error('Zip not initialized');
    }
    
    console.log('[DownloadAsZipStrategy] Generating zip file...');
    
    // Generate zip with streaming to minimize memory usage
    const zipBlob = await this.zip.generateAsync({
      type: 'blob',
      compression: 'DEFLATE',
      compressionOptions: { level: 6 },
      streamFiles: true
    }, (metadata) => {
      // Progress callback
      console.log(`[DownloadAsZipStrategy] Zip progress: ${metadata.percent.toFixed(1)}%`);
    });
    
    console.log(`[DownloadAsZipStrategy] Zip generated (${(zipBlob.size / 1024 / 1024).toFixed(2)} MB)`);
    
    // Download the zip file
    const blobUrl = URL.createObjectURL(zipBlob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = `tinygrid-export-${Date.now()}.zip`;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    }, 1000);
    
    console.log('[DownloadAsZipStrategy] Zip downloaded');
  }
}

/**
 * Factory function to get strategy instance
 * @param {string} strategyName - Strategy name ('original'|'folder'|'download_all'|'zip')
 * @param {object} editorState - Editor state instance
 * @returns {ExportStrategy} Strategy instance
 */
export function getExportStrategy(strategyName, editorState) {
  switch (strategyName) {
    case 'original':
      return new SaveWithOriginalFilesStrategy(editorState);
    case 'folder':
      return new DownloadToFolderStrategy(editorState);
    case 'download_all':
      return new DownloadAllStrategy(editorState);
    case 'zip':
      return new DownloadAsZipStrategy(editorState);
    default:
      throw new Error(`Unknown export strategy: ${strategyName}`);
  }
}

